import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useModal } from '../App';
import { useI18n } from '../lib/i18n';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHeroZone, setIsHeroZone] = useState(true);
  const location = useLocation();
  const { openModal } = useModal();
  const { lang, toggleLang, t } = useI18n();

  const isHome = location.pathname === '/' || location.pathname.startsWith('/noticias/') || location.pathname.startsWith('/artigos/');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setIsHeroZone(window.scrollY < window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsHeroZone(true); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const transparent = isHome && isHeroZone && !isOpen;
  const textColor = transparent ? 'text-white' : 'text-black';

  const handleModalNav = (modal) => {
    setIsOpen(false);
    // If not on home, navigate there first
    if (location.pathname !== '/') {
      window.location.href = '/';
      setTimeout(() => openModal(modal), 300);
    } else {
      openModal(modal);
    }
  };

  const navItems = [
    { label: t('nav.instituto', 'O Instituto'), action: () => handleModalNav('instituto') },
    { label: t('nav.solucoes', 'Soluções'), action: () => handleModalNav('solucoes') },
    { label: t('nav.projetos', 'Projetos'), action: () => handleModalNav('projetos') },
    { label: t('nav.deia', 'DEI&A'), action: () => handleModalNav('deia') },
    { label: t('nav.emfoco', 'Em Foco'), action: () => handleModalNav('emfoco') },
    { label: t('nav.artigos', 'Artigos'), action: () => handleModalNav('artigos') },
  ];

  const mobileItemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i) => ({
      opacity: 1, x: 0,
      transition: { delay: i * 0.06, duration: 0.4, ease: [0.4, 0, 0.2, 1] },
    }),
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          transparent
            ? 'bg-transparent border-b border-transparent'
            : `glass-header ${scrolled ? 'scrolled' : ''}`
        }`}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-[1400px]">
          <div className="flex items-center justify-between h-16 sm:h-20 md:h-24">
            {/* Logo — local, dual-layer crossfade, tamanho mínimo p/ legibilidade das linhas finas */}
            <Link to="/" className="flex items-center" onClick={() => setIsOpen(false)}>
              <motion.div
                className="relative"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
              >
                <img
                  src="/logo-ibrc-white.png"
                  alt="IBRC — Instituto BR Cultural"
                  width={2037}
                  height={901}
                  className={`h-[2.2rem] sm:h-[2.8rem] md:h-[3.4rem] w-auto transition-opacity duration-500 ${transparent ? 'opacity-100' : 'opacity-0'}`}
                />
                <img
                  src="/logo-ibrc-dark.png"
                  alt="IBRC — Instituto BR Cultural"
                  width={2037}
                  height={901}
                  className={`h-[2.2rem] sm:h-[2.8rem] md:h-[3.4rem] w-auto absolute inset-0 transition-opacity duration-500 ${transparent ? 'opacity-0' : 'opacity-100'}`}
                />
              </motion.div>
            </Link>

            {/* Desktop Nav */}
            <nav aria-label={lang === 'pt' ? 'Navegação principal' : 'Main navigation'} className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                item.isLink ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={item.action}
                    className={`font-oswald uppercase text-sm tracking-widest hover:text-[#FFC107] transition-colors accent-line cursor-pointer ${textColor}`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={`font-oswald uppercase text-sm tracking-widest hover:text-[#FFC107] transition-colors accent-line cursor-pointer bg-transparent border-none ${textColor}`}
                  >
                    {item.label}
                  </button>
                )
              ))}
              {/* Language Toggle — Desktop */}
              <button
                onClick={toggleLang}
                aria-label={lang === 'pt' ? 'Switch to English' : 'Mudar para Português'}
                className="flex items-center h-9 border border-white/10 hover:border-[#FFC107]/40 transition-all overflow-hidden"
              >
                <span className={`px-3 py-1.5 text-xs font-bold tracking-wider transition-all ${
                  lang === 'pt' ? 'bg-[#FFC107] text-black' : `bg-transparent ${textColor} opacity-40`
                }`}>PT</span>
                <span className={`px-3 py-1.5 text-xs font-bold tracking-wider transition-all ${
                  lang === 'en' ? 'bg-[#FFC107] text-black' : `bg-transparent ${textColor} opacity-40`
                }`}>EN</span>
              </button>

              <motion.a
                href="https://area-aluno.institutobrcultural.org"
                target="_blank"
                rel="noopener noreferrer"
                className={`h-9 px-5 uppercase tracking-widest text-[10px] font-semibold transition-all flex items-center btn-spring backdrop-blur-sm ${
                  transparent
                    ? 'bg-[#FFC107]/70 text-black hover:bg-white/80'
                    : 'bg-black/60 text-white hover:bg-[#FFC107]/70 hover:text-black'
                }`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {t('nav.areaaluno', 'Área do Aluno')}
              </motion.a>
            </nav>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? (lang === 'pt' ? 'Fechar menu' : 'Close menu') : (lang === 'pt' ? 'Abrir menu' : 'Open menu')}
              aria-expanded={isOpen}
              className={`lg:hidden p-2 z-50 ${isOpen ? 'text-white' : textColor}`}
              whileTap={{ scale: 0.9 }}
            >
              {isOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
            </motion.button>
          </div>
        </div>
      </header>

      {/* Fullscreen Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.3 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            className="fixed inset-0 z-40 mobile-menu-overlay flex flex-col justify-center px-8"
          >
            <nav className="space-y-2">
              {navItems.map((item, i) => (
                <motion.div
                  key={item.label}
                  custom={i}
                  variants={mobileItemVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {item.isLink ? (
                    <Link
                      to={item.to}
                      onClick={item.action}
                      className="block py-3 font-oswald text-3xl uppercase text-white hover:text-[#FFC107] transition-colors"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      onClick={item.action}
                      className="block py-3 font-oswald text-3xl uppercase text-white hover:text-[#FFC107] transition-colors bg-transparent border-none text-left w-full"
                    >
                      {item.label}
                    </button>
                  )}
                </motion.div>
              ))}
              <motion.div
                custom={navItems.length}
                variants={mobileItemVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Language Toggle — Mobile */}
                <div className="flex items-center gap-4 mt-8 mb-4">
                  <button
                    onClick={toggleLang}
                    className="flex items-center h-12 border border-white/20"
                  >
                    <span className={`px-5 py-2.5 text-sm font-bold tracking-wider transition-all ${
                      lang === 'pt' ? 'bg-[#FFC107] text-black' : 'text-white/30'
                    }`}>PT</span>
                    <span className={`px-5 py-2.5 text-sm font-bold tracking-wider transition-all ${
                      lang === 'en' ? 'bg-[#FFC107] text-black' : 'text-white/30'
                    }`}>EN</span>
                  </button>
                </div>
                <a
                  href="https://area-aluno.institutobrcultural.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block h-11 px-7 bg-[#FFC107]/70 text-black uppercase tracking-widest text-[11px] font-semibold hover:bg-white/80 transition-all flex items-center btn-spring"
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.areaaluno', 'Área do Aluno')}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
